<template>
  <div v-if="signers.length" class="approvers">
    <h3>{{ i18n.lbl_signatures() }}</h3>
    <div
      v-for="signer in signers"
      :key="signer.r"
      :class="[
        'sign-row',
        {
        'rejected': signer.status === 'canceled',
        confirm: signer.status === 'confirmed',
        pending: signer.isPendingSlot
      }]"
    >
      <span v-if="signer.status === 'confirmed'">
        <img src="/static/img/level-success.svg"/>
      </span>
      <span v-else-if="signer.isPendingSlot" class="hourglass-icon">
        <img src="/static/img/hourglass-pending.svg"/>
      </span>
      <span v-else-if="signer.isFutureSlot" class="hourglass-icon">
        <img src="/static/img/hourglass-muted.svg"/>
      </span>
      <span v-else>
        <img v-if="amlState && Number(signer.level) === 1" src="/static/img/level-success.svg"/>
        <img v-else src="/static/img/level-cross.svg"/>
      </span>
      <template v-if="signer.isPendingSlot">
        <strong>{{ signer.name_of }} – {{ i18n.lbl_check_sign() }}</strong>
      </template>
      <template v-else-if="signer.isFutureSlot">
        <span>{{ signer.name_of }} – {{ i18n.lbl_check_sign() }}</span>
      </template>
      <template v-else>
        <span>{{ signer.name_of }}</span>
        <span>{{ signer.approver_email }}</span>
        <span>{{ signer.approved_at }}</span>
        <template v-if="amlState && Number(signer.level) === 1">
          &nbsp;
          <span :class="['aml-status', {'aml-status--pending': isAutoAMLCheckPending}]">
            <md-icon class="scsc">search</md-icon>
          </span>
          <strong>{{ amlState }}</strong>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import { withdrawUSDTService } from '@api/withdraw_usdt.service'
import { i18n } from '@js/i18n'

export default {
  name: 'CheckSignList',
  props: {
    id: {
      default: 0
    },
    isHistory: {
      default: false
    },
    signs: {
      type: [Array, Object],
      default: null
    },
    totalLevels: {
      type: [Number, String],
      default: 0
    },
    currentLevel: {
      type: [Number, String],
      default: 0
    },
    isPending: {
      type: Boolean,
      default: false
    },
    withdrawalStatus: {
      type: String,
      default: ''
    },
    amlStatus: {
      type: String,
      default: ''
    }
  },
  emits: ['getIds'],
  data () {
    return {
      signers: [],
      amlState: '',
      resolvedWithdrawalStatus: '',
      resolvedAmlStatus: '',
      i18n
    }
  },
  computed: {
    isAutoAMLCheckPending () {
      return ['pending', 'check_again', 'initialised'].includes(this.resolvedWithdrawalStatus) &&
        this.resolvedAmlStatus === ''
    }
  },
  async mounted () {
    let values = this.formatSigns(this.signs)
    if (values) {
      this.setSigners(values, true)
      return
    }

    if (this.isHistory) {
      const { data } = await withdrawUSDTService.getSignForHistory(this.id)
      values = data
    } else {
      const { data } = await withdrawUSDTService.getSigns(this.id)
      values = data
    }
    const approvalDetails = await this.getApprovalDetails()
    this.setAmlState(approvalDetails)
    this.setSigners(values, false, approvalDetails)
  },
  methods: {
    async getApprovalDetails () {
      if (this.totalLevels || !this.id) return null

      try {
        const { data } = this.isHistory
          ? await withdrawUSDTService.getWithdrawalDetailsByInternalId(this.id)
          : await withdrawUSDTService.getPendingById(this.id)
        return data
      } catch (error) {
        return null
      }
    },
    setSigners (values, isPassedSigns, approvalDetails = null) {
      const signers = values.map(el => {
        return {
          ...el,
          r: isPassedSigns ? el.r || el.approver_id : el.r,
          status: isPassedSigns ? el.status || 'confirmed' : el.status,
          approved_at: this.i18n.d(el.approved_at),
          name_of: this.formatName(el.level, isPassedSigns)
        }
      })
      this.signers = isPassedSigns ? signers : this.addSignatureSlots(signers, approvalDetails)
      this.$emit('getIds', this.signers.map(el => el.approver_id))
    },
    addSignatureSlots (signers, approvalDetails) {
      const totalLevels = Number(this.totalLevels || (approvalDetails && approvalDetails.approval_level))
      const currentLevel = Number(this.currentLevel || (approvalDetails && approvalDetails.current_approval_level))
      const isPending = this.isPending || (approvalDetails && approvalDetails.status === 'check_sign')

      if (!totalLevels) return signers

      const rejectedSigner = signers.find(signer => signer.status === 'canceled')
      const lastLevel = rejectedSigner ? Number(rejectedSigner.level) : totalLevels
      const slots = []

      for (let level = 1; level <= lastLevel; level++) {
        const levelSigners = signers.filter(signer => Number(signer.level) === level)
        if (levelSigners.length) {
          slots.push(...levelSigners)
        } else if (isPending && level === currentLevel) {
          slots.push({
            r: `pending-${level}`,
            level,
            status: 'pending',
            isPendingSlot: true,
            name_of: this.formatName(level)
          })
        } else {
          slots.push({
            r: `future-${level}`,
            level,
            isFutureSlot: true,
            name_of: this.formatName(level)
          })
        }
      }

      return slots
    },
    setAmlState (approvalDetails) {
      const status = this.withdrawalStatus || (approvalDetails && approvalDetails.status)
      const amlStatus = this.amlStatus || (approvalDetails && approvalDetails.aml_status)
      this.resolvedWithdrawalStatus = status || ''
      this.resolvedAmlStatus = amlStatus || ''

      if (['check_again', 'initialised', 'pending'].includes(status) && this.resolvedAmlStatus === '') {
        this.amlState = this.i18n.lbl_aml_pending()
      } else if (status === 'pending' && ['attention', 'check_failed'].includes(this.resolvedAmlStatus)) {
        this.amlState = this.i18n.lbl_manual_aml_review()
      }
    },
    formatSigns (signs) {
      if (Array.isArray(signs)) return signs
      if (signs) return [signs]
      return null
    },
    formatName (lvl, isPassedSigns = false) {
      switch (lvl) {
        case 1:
          return this.i18n.lbl_level_1()
        case 2:
          // return this.i18n.lbl_level_1()
          return this.i18n.lbl_level_2()
        case 3:
          // return this.i18n.lbl_level_2()
          return this.i18n.lbl_level_3()
        case 4:
          return this.i18n.lbl_level_4()
        default:
          return isPassedSigns ? undefined : `${this.i18n.lbl_level()} ${lvl}`
      }
    },
    formatTimestamp (isoString) {
      const date = new Date(isoString)
      const yy = String(date.getUTCFullYear()).slice(-2)
      const mm = String(date.getUTCMonth() + 1).padStart(2, '0')
      const dd = String(date.getUTCDate()).padStart(2, '0')
      const hh = String(date.getUTCHours()).padStart(2, '0')
      const min = String(date.getUTCMinutes()).padStart(2, '0')
      const sec = String(date.getUTCSeconds()).padStart(2, '0')
      return `${yy}-${mm}-${dd} ${hh}:${min}:${sec}`
    }
  }
}
</script>

<style scoped lang="scss">
@import '~scss/variables';
.sign-row {
  min-height: 25px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.sign-row:not(:has(.confirm)),
.sign-row:not(:has(.rejected)) {
  padding: 1px 0px;
}

.approvers {
  margin-top: 20px;

  img {
    max-width: 25px;
  }
}

.rejected {
  &:last-child {
    color: $error-red;
  }
}

.hourglass-icon {
  padding-left: 2px;

  img {
    width: 22px;
    height: 22px;
  }
}

.aml-status {
  max-width: 22px;
  width: 22px;
  background: $col-pending;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 22px;

  .md-icon {
    font-size: 18px !important;
    font-weight: 600;
    height: 20px;
    min-width: 20px;
    width: 20px;
    color: $color-graphite !important;
  }

  &.aml-status--pending {
    background: $color-mint;
  }
}
</style>



// WEBPACK FOOTER //
// src/vue/app/widrawalRequest/CheckSignList.vue